import ContentPage from '@/components/feature/ContentPage';

export default function CouponPage() {
  return (
    <ContentPage
      title="CSSBuy Coupon: How to Find Current Discounts"
      metaTitle="CSSBuy Coupon & Discount Code Guide 2026 | CSSBuyBR"
      metaDescription="CSSBuy coupon guide. Learn how coupons work, where to verify offers, restrictions and expiration. Never invent coupon codes — always verify."
      subtitle="Coupons and discount codes can help reduce costs, but always verify current offers through official channels."
      showToc
      sections={[
        { id: 'how', title: 'How Coupons Work' },
        { id: 'verify', title: 'Verifying Offers' },
        { id: 'restrictions', title: 'Restrictions' },
        { id: 'tips', title: 'Tips for Finding Coupons' },
      ]}
      content={
        <div className="space-y-6">
          <section id="how">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">How CSSBuy Coupons Work</h2>
            <p>Coupons and discount codes can reduce your total costs on CSSBuy, but understanding how they work is important:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Coupons may apply to service fees, shipping, or specific product categories</li>
              <li>Some codes are one-time use only</li>
              <li>Stacking multiple coupons may not always be possible</li>
              <li>Minimum order values may apply for certain discounts</li>
              <li>Coupons typically have expiration dates</li>
            </ul>
          </section>

          <section id="verify">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Verifying Current Offers</h2>
            <p>Coupon codes found online may be expired, invalid, or even fraudulent. To verify current offers:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Check the official CSSBuy website promotions page',
                'Subscribe to official newsletters for current codes',
                'Follow verified social media accounts',
                'Check community forums for user-shared working codes',
                'Verify expiration dates before applying',
                'Avoid third-party sites that list unverified codes',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-foreground-500 italic">Note: This resource does not list active coupon codes. Always verify current codes through official channels to avoid disappointment or potential scams.</p>
          </section>

          <section id="restrictions">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Common Coupon Restrictions</h2>
            <p>Most coupons come with terms and conditions. Common restrictions include:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Minimum order value requirements</li>
              <li>Category exclusions (some items may not be eligible)</li>
              <li>New customer vs. returning customer restrictions</li>
              <li>Geographic limitations (certain countries only)</li>
              <li>Time-limited validity windows</li>
              <li>One-time use per account</li>
            </ul>
          </section>

          <section id="tips">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Tips for Finding Valid Coupons</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Sign up for official newsletters',
                'Join community forums where users share deals',
                'Check during major sale events (Black Friday, etc.)',
                'Follow official accounts on social media',
                'Look for referral programs that reward both parties',
                'Combine coupons with other promotions when allowed',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-accent-50 text-sm text-foreground-700">
                  <i className="ri-lightbulb-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200/70">
            <p className="text-sm text-secondary-700 font-medium">
              <i className="ri-alert-line mr-1"></i>
              Never trust coupon codes from unverified sources. Scammers may use fake coupons to steal your account or payment information. Always verify through official channels.
            </p>
          </div>

          <div className="mt-6 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Find Current Deals</h3>
            <p className="text-sm text-primary-700 mb-3">Visit the main site to see the latest promotions and offers available right now.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}